import { Marker } from "maplibre-gl";
import type {
  EaseToOptions,
  LayerSpecification,
  Map,
  MapGeoJSONFeature,
  PointLike,
} from "maplibre-gl";
import type { LngLat } from "../../../lib/geo/types.ts";

const SELECTION_OVERLAY_SOURCE = "__sel_source";
const SELECTION_MARKER_URL = new URL(
  "../../../assets/map-pins/result-pin.svg",
  import.meta.url,
).href;

export function attachMapSelectedFeaturePresentation(
  map: Map,
): {
  showRenderedSelection: (feature: MapGeoJSONFeature) => void;
  clearRenderedSelection: () => void;
  clearSelection: () => void;
  focusLngLat: (lngLat: LngLat, offset?: PointLike) => void;
  destroy: () => void;
} {
  let marker: Marker | undefined;
  let selectionOverlayLayerIds: string[] = [];

  // Clones each symbol layer that renders the selected feature into a GeoJSON-
  // backed source. This keeps the icon and label visible when zooming out past
  // the layer's minzoom — vector tile data is unavailable below certain zoom
  // levels, so a minzoom override alone doesn't help; GeoJSON data lives in
  // memory and is always available.
  function addSelectionOverlayLayers(
    feature: MapGeoJSONFeature,
  ): void {
    if (feature.id === undefined || !feature.sourceLayer) return;

    map.addSource(SELECTION_OVERLAY_SOURCE, {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: feature.geometry,
        properties: feature.properties,
      },
    });

    const layers = map.getStyle()?.layers ?? [];
    for (const layer of layers) {
      if (layer.type !== "symbol") continue;
      const sl = (layer as { "source-layer"?: string })["source-layer"];
      if (sl !== feature.sourceLayer) continue;
      if ((layer as { source?: string }).source !== feature.source) continue;

      const cloneId = `__sel_${layer.id}`;
      if (map.getLayer(cloneId)) continue;

      // Strip source-layer (invalid on GeoJSON) and any existing filter
      // (the source has only one feature).
      const {
        "source-layer": _sl,
        filter: _f,
        ...rest
      } = layer as Record<string, unknown>;
      map.addLayer({
        ...rest,
        id: cloneId,
        source: SELECTION_OVERLAY_SOURCE,
        minzoom: 0,
      } as LayerSpecification);
      selectionOverlayLayerIds.push(cloneId);
    }
  }

  function clearRenderedSelection(): void {
    for (const id of selectionOverlayLayerIds) {
      if (map.getLayer(id)) map.removeLayer(id);
    }
    selectionOverlayLayerIds = [];
    if (map.getSource(SELECTION_OVERLAY_SOURCE)) {
      map.removeSource(SELECTION_OVERLAY_SOURCE);
    }
  }

  function showRenderedSelection(feature: MapGeoJSONFeature): void {
    clearRenderedSelection();
    if (feature.id !== undefined && feature.sourceLayer) {
      addSelectionOverlayLayers(feature);
    }
  }

  function clearSelection(): void {
    clearRenderedSelection();
    removeMarker();
  }

  function focusLngLat(
    lngLat: LngLat,
    offset?: PointLike,
  ): void {
    setMarker(lngLat);
    // TODO: When PostPass returns GeoJSON geometry, draw it here and fit the
    // camera to the rendered geometry bounds instead of centering on the
    // point-on-surface anchor.
    map.easeTo(selectionEaseOptions(lngLat, map.getZoom(), offset));
  }

  function setMarker(lngLat: LngLat): void {
    removeMarker();

    const el = document.createElement("div");
    el.className = "selection-marker";

    const img = document.createElement("img");
    img.src = SELECTION_MARKER_URL;
    img.alt = "";
    img.width = 28;
    img.height = 40;
    img.draggable = false;
    img.style.display = "block";

    el.append(img);
    marker = new Marker({
      element: el,
      anchor: "bottom",
      offset: [0, -16],
    })
      .setLngLat(lngLat)
      .addTo(map);
  }

  function removeMarker(): void {
    marker?.remove();
    marker = undefined;
  }

  function destroy(): void {
    clearSelection();
  }

  return {
    showRenderedSelection,
    clearRenderedSelection,
    clearSelection,
    focusLngLat,
    destroy,
  };
}

export function selectionEaseOptions(
  lngLat: LngLat,
  currentZoom: number,
  offset: PointLike | undefined,
): EaseToOptions {
  return {
    center: lngLat,
    zoom: Math.max(currentZoom, 17),
    duration: 300,
    ...(offset ? { offset } : {}),
  };
}
